<template>

    <v-dialog v-model="model"
              persistent
              max-width="1600">

        <v-card :min-height="dialogMinHeight ? dialogMinHeight : null">

            <toolbar-page title="Participants" icon="mdi-account-group" help="project-participants" color="#252525">
                <template slot="buttons">

                    <div class="d-flex">

                        <v-tooltip color="primary" max-width="400" top v-if="hasPermission('export project participants')">
                            <template v-slot:activator="{ on }">
                                <v-btn v-on="on"
                                       class="align-self-center mr-2"
                                       @click="exportClicked()"
                                       text
                                       small
                                       v-if="hasPermission('view projects')">
                                    <v-icon left>mdi-download</v-icon>
                                    Export
                                </v-btn>
                            </template>
                            Export a list of participants and their statuses
                        </v-tooltip>

                        <v-switch class="align-self-center mr-10 body-2"
                                  hide-details
                                  v-model="showIdsModel"
                                  @change="showIdsToggleChanged"
                                  dense>

                            <template v-slot:label>
                                <span :class="showIdsModel ? 'body-2 white--text' : 'body-2 grey--text'">
                                    Show ID
                                </span>
                            </template>
                        </v-switch>

                        <!-- FILTER BUTTONS -->
                        <v-btn-toggle v-model="selectedStatusModel"
                                      class="ml-5 mr-5 align-self-center"
                                      dense
                                      mandatory
                                      style="background-color: #333;"
                                      @change="filtersChanged">

                            <v-btn text>
                                Invited
                            </v-btn>

                            <v-btn text>
                                Started
                            </v-btn>

                            <v-btn text>
                                Security
                            </v-btn>

                            <v-btn text>
                                Profile
                            </v-btn>

                            <v-btn text>
                                Quota
                            </v-btn>

                            <v-btn text>
                                Quality
                            </v-btn>

                            <v-btn text>
                                Duplicate
                            </v-btn>

                            <v-btn text>
                                Completed
                            </v-btn>

                        </v-btn-toggle>

                        <v-tooltip top color="primary">
                            <template v-slot:activator="{ on }">
                                <v-btn @click="reloadClicked"
                                       icon
                                       v-on="on">
                                    <v-icon>mdi-reload</v-icon>
                                </v-btn>
                            </template>
                            <span>Reload participants</span>
                        </v-tooltip>

                        <v-tooltip top color="primary">
                            <template v-slot:activator="{ on }">
                                <v-btn @click="close()"
                                       v-on="on"
                                       icon>
                                    <v-icon>mdi-close</v-icon>
                                </v-btn>
                            </template>
                            <span>Close this dialog</span>
                        </v-tooltip>

                    </div>
                </template>
            </toolbar-page>

            <!-- ALERT ABOUT FILTERS -->
            <v-alert v-show="!loading && tableData.length === 0" type="info" class="body-2 ma-5">
                No participants found. Check your filter settings up top!
            </v-alert>

            <!-- LOADING ANIMATION IF NO ROWS -->
            <div v-show="loading && tableData.length === 0" style="padding-top: 180px;">
                <chart-loader style="margin-left: 40%"
                              :height="100"
                              :width="300"
                              :loading="true"
                ></chart-loader>
            </div>

            <!-- DATA TABLE -->
            <v-data-table v-show="tableData.length > 0"
                          :headers="headers"
                          :items="tableData"
                          :loading="loading"
                          :options.sync="options"
                          dense
                          disable-sort
                          :server-items-length="participantCount"
                          :footer-props.sync="footerProps"
                          no-results-text="No participants found!"
                          no-data-text="No participants found!">

                <template v-slot:body="{ items, headers }">

                    <tbody>

                    <tr v-for="p in items">

                        <td v-show="showIdsModel" class="id-field truncate">
                            <v-tooltip top max-width="300" open-delay="200" color="primary">
                                <template v-slot:activator="{ on }">
                                    <div v-on="on" @click="copy(p.panelist_id)" class="cursor-pointer grey--text text--lighten-2">
                                        {{ p.panelist_id }}
                                    </div>
                                </template>
                                Click to copy to clipboard
                            </v-tooltip>
                        </td>

                        <td class="text-no-wrap" v-if="project.type==='router'">
                            {{ p.supplier_name }}
                        </td>

                        <!-- PARTICIPANT NAME -->
                        <td class="text-no-wrap" v-if="project.type!=='router'">
                            {{ p.name }}
                        </td>

                        <!-- STATUS -->
                        <td>
                            <v-chip small :color="statusColor(p.activity_record.status)">
                                {{ p.activity_record.status.toUpperCase() }}
                            </v-chip>
                        </td>

                        <!-- INTERVIEW TIME -->
                        <td>
                            <v-chip v-if="p.interview_time_min" small :color="interviewTimeChipColor(p.interview_time_min)">
                                {{ p.interview_time_min }} min
                            </v-chip>
                            <v-chip v-else small>
                                N/A
                            </v-chip>
                        </td>

                        <!-- REWARD -->
                        <td v-if="project.type!=='router'">
                            <v-chip v-if="p.activity_record.reward" small label color="primary">
                                {{ p.activity_record.reward }}
                            </v-chip>
                            <v-chip v-else small>
                                N/A
                            </v-chip>
                        </td>

                        <!-- LOCATION -->
                        <td class="text-no-wrap">
                            {{ p.city }} {{ p.region }}, {{ p.country }}
                        </td>

                        <!-- FLAGS -->
                        <td class="truncate">
                            <security-events v-if="p.security_events"
                                             :security-events="p.security_events"
                            ></security-events>
                        </td>

                        <td class="text-no-wrap">
                            {{ toHumanFrom(p.activity_record.created_at) }}
                        </td>

                        <td>
                            <v-menu offset-y>
                                <template v-slot:activator="{ on }">
                                    <v-btn
                                        icon
                                        class="ma-0"
                                        text
                                        v-on="on"
                                        color="secondary">
                                        <v-icon light color="white">mdi-dots-vertical</v-icon>
                                    </v-btn>
                                </template>

                                <v-list class="pa-0">

                                    <!-- VIEW PARTICIPANTS -->
                                    <v-list-item
                                        @click="editPanelistClicked(p.panelist_id)">
                                        <v-list-item-title>
                                            Edit panelist
                                        </v-list-item-title>
                                    </v-list-item>

                                    <v-list-item v-if="p.activity_record.status === 'completed'"
                                                 @click="disqualify(p.panelist_id, 'quality')">
                                        <v-list-item-title>
                                            Disqualify (quality)
                                        </v-list-item-title>
                                    </v-list-item>

                                    <v-list-item v-if="p.activity_record.status === 'completed'"
                                                 @click="disqualify(p.panelist_id, 'duplicate')">
                                        <v-list-item-title>
                                            Disqualify (duplicate)
                                        </v-list-item-title>
                                    </v-list-item>

                                </v-list>
                            </v-menu>
                        </td>
                    </tr>

                    </tbody>

                </template>
            </v-data-table>

            <json v-if="false" :data="selectedStatusModel"></json>

        </v-card>

    </v-dialog>

</template>

<script>

import ToolbarPage from "../../../components/UI/ToolbarPage";
import {mapActions} from 'vuex';
import Json from 'vue-json-pretty';
import {timeDisplay} from "../../../components/mixins/timeDisplayMixin";
import ChartLoader from "../../../components/UI/ChartLoader";
import {clipboardMixin} from "../../../components/mixins/clipboardMixin";
import SecurityEvents from "./SecurityEvents";
import {statusColorMixin} from "../../../components/mixins/statusColorMixin";

export default {

    mixins: [timeDisplay, clipboardMixin, statusColorMixin],

    name: "ParticipantsDialog",

    components: {
        SecurityEvents,
        ChartLoader,
        Json,
        ToolbarPage
    },

    props: {
        value: {
            type: Boolean,
            required: true,
        },

        project: {
            type: Object,
            required: true,
        }
    },

    data() {
        return {
            participants: [],
            speedingThresholdMin: null,
            cursors: [],
            page: 1,
            participantCount: 0,
            options: null,
            loading: false,
            completesOnlyFilter: false,
            selectedStatusModel: [0],
            showIdsModel: false,
            statuses: ['invited', 'started', 'security', 'profile', 'quota', 'quality', 'duplicate', 'completed'],
            footerProps: {
                itemsPerPageOptions: [15]
            },
        }
    },

    watch: {
        options: {
            handler() {
                this.getDataFromApi()
            },
            deep: true,
        },

        project() {
            this.load();
        }
    },

    computed: {

        model: {
            get() {
                return this.value;
            },
            set() {
                this.close();
            }
        },

        routerEnabled() {
            return this.project.type === "router";
        },

        headers() {

            let headers = [
                {name: 'id', text: 'ID', sortable: false, width: 350},
                {name: 'supplier_name', text: 'SUPPLIER', sortable: false, width: 150},
                {name: 'name', text: 'NAME', sortable: true},
                {name: 'status', text: 'STATUS', sortable: true, width: 100},
                {name: 'interview_time_min', text: 'TIME', sortable: true, width: 100},
                {name: 'reward', text: 'REWARD', sortable: true, width: 100},
                {name: 'location', text: 'LOCATION', sortable: true, width: 120},
                {name: 'flags', text: 'FLAGS', sortable: false},
                {name: 'created_by', text: 'LAST STATUS', sortable: false, width: 150},
                {text: '', sortable: false, width: 20},
            ];

            if (this.routerEnabled) {
                headers = headers.filter(header => !['name', 'reward'].includes(header.name));
            } else {
                headers = headers.filter(header => !['supplier_name'].includes(header.name));
            }

            if (!this.showIdsModel) headers = headers.filter(header => header.name !== 'id')

            return headers;
        },

        dialogMinHeight() {
            if (this.participants.length < 15) {
                const headerHeight = 32;
                const footerHeight = 37;
                const rows = 36 * 15;
                return headerHeight + footerHeight + rows;
            }
            return null;
        },

        tableData() {
            return this.participants;
        },
    },

    methods: {

        ...mapActions('ProjectManager', [
            'loadParticipants',
            'disqualifyParticipants',
            'exportParticipants'
        ]),

        copy(value) {
            this.copyToClipboard(value);
            Bus.$emit('flash-message', {message: 'Panelist identifier copied to the clipboard'});
        },

        disqualify(panelistId, reason) {

            const config = {
                projectId: this.project.id,
                reason: reason,
                panelistIds: [panelistId]
            }

            this.disqualifyParticipants(config)
                .then((response) => {

                    if (response.data.updated_count === 1) {

                        let index = this.participants.findIndex(p => p.panelist_id === panelistId);

                        let participant = this.participants[index];
                        participant.status = reason;
                        participant.activity_record.status = reason;
                        participant.activity_record.reward = 0;
                        participant.activity_record.revenue = 0;

                        this.participants.splice(index, 1, participant);
                    } else {
                        Bus.$emit('flash-message', {type: "error", message: "Server rejected disqualification"})
                    }
                })
                .catch((error) => {
                    Bus.$emit('flash-message', {type: "error", message: "OOPS We failed to disqualify"});
                });
        },

        getDataFromApi() {
            if (this.loading === false) {
                if (this.options.page === 1) {
                    this.load();
                } else {
                    const cursor = this.cursors.find(cursor => cursor.page === this.options.page - 1);

                    if (cursor) {
                        this.load(cursor.cursor);
                    } else {
                        this.load();
                    }
                }
            }
        },

        reloadClicked() {
            this.getDataFromApi();
        },

        filtersChanged() {
            this.cursors = [];
            this.options.page = 1;
            this.getDataFromApi();

            localStorage.projectParticipantsFilter = JSON.stringify(this.selectedStatusModel);
        },

        close() {
            this.$emit('input', false);
        },

        statusColor(status) {
            if (this.statusColors[status]) return this.statusColors[status];
            return "grey darken-2";
        },

        interviewTimeChipColor(interviewTimeMin) {

            if (interviewTimeMin <= this.speedingThresholdMin) {
                return '#BF360C';
            } else {
                return '#388E3C';
            }
        },

        completesOnlyChanged() {
            this.getDataFromApi();
        },

        load(cursor = null) {
            this.loading = true;

            const data = {
                projectId: this.project.id,
                cursor: cursor,
                filter: this.statuses[this.selectedStatusModel]
            }

            this.loadParticipants(data)
                .then((response) => {
                    this.participants = response.data.participants;
                    this.speedingThresholdMin = response.data.speeding_threshold_min;
                    this.addCursor(this.options.page, response.data.cursor);
                    this.participantCount = response.data.number_of_participants;
                })
                .catch((error) => {
                    Bus.$emit('flash-message', {type: "error", message: "OOPS We failed to load project participants"});
                })
                .finally(() => {
                    this.loading = false;
                })
        },

        addCursor(page, cursor) {
            let found = this.cursors.find(cursor => cursor.page === page);

            if (!found) {
                this.cursors.push({page: page, cursor: cursor});
            }
        },

        editPanelistClicked(id) {
            this.$router.push({
                name: 'panelist-manager-edit',
                params: {id: id}
            });
        },

        showIdsToggleChanged(toggled) {

            localStorage.setItem('participantListShowIds', toggled);

            Bus.$emit('flash-message', {
                location: 'bottom',
                message: 'We will remember this selection for you'
            });
        },

        exportClicked() {

            const data = {
                projectId: this.project.id,
                status: this.statuses[this.selectedStatusModel]
            }

            this.exportParticipants(data)
            .then(() => {
                Bus.$emit('flash-message', {
                    location: 'top',
                    message: 'Participant export is now running... The file be placed in the downloads'
                });
            })
        }
    },

    created() {

        const setting = localStorage.getItem('projectParticipantsFilter');

        if (setting) {
            this.selectedStatusModel = JSON.parse(setting);
        }

        this.showIdsModel = localStorage.getItem('participantListShowIds');
    },
}
</script>

<style scoped>

.id-field {
    font-family: monospace;
    font-size: 0.9em;
}

.truncate {
    max-width: 1px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}
</style>
